<script lang="ts">
    import Spinner from "@components_v2/Spinner/Spinner.svelte";
    import Text from "@components_v2/Typography/Text.svelte";
    import type { TextTone } from "@components_v2/Typography/types";
    import CN from "classnames";

    import Icon from "../Icon.svelte";
    import UnstyledButton from "./UnstyledButton.svelte";

    type ButtonMode = "primary" | "secondary" | "tertiary" | "link";
    type ButtonSize = "regular" | "small";
    type ButtonType = "button" | "submit" | "reset";
    type ButtonTarget = "_self" | "_blank" | "_parent" | "_top";

    export let className = "";
    export { className as class };

    export let size: ButtonSize = "regular";
    export let mode: ButtonMode = "primary";
    export let type: ButtonType = "button";
    export let tone: TextTone = "inherit";
    export let fullWidth = false;
    export let disabled = false;
    export let href: string | null = null;
    export let target: ButtonTarget | null = null;
    export let rel: string | null = null;
    export let role: string | null = null;
    export let loading = false;
    export let testId: string | null = null;
    export let ariaLabel: string | null = null;
    export let ariaPressed: boolean | null = null;
    export let ariaExpanded: boolean | null = null;
    export let ariaControls: string | null = null;
    export let ariaSelected: boolean | null = null;

    $: isDisabled = disabled || loading;
    $: classes = CN(
        "gw-btn",
        `gw-btn--mode-${mode}`,
        `gw-btn--size-${size}`,
        { "gw-btn--full-width": fullWidth },
        { "gw-btn--disabled": isDisabled },
        { "gw-btn--loading": loading },
        className
    );
</script>

<UnstyledButton
    class={classes}
    {href}
    disabled={isDisabled}
    {type}
    {target}
    {rel}
    {role}
    {ariaLabel}
    {ariaPressed}
    {ariaExpanded}
    {ariaControls}
    {ariaSelected}
    ariaBusy={loading}
    data-testid={testId}
    on:click
    on:mouseover
    on:focus
    on:mousedown
    on:mouseup
    on:mouseleave
    on:blur
    {...$$restProps}
>
    {#if $$slots["icon-left"]}
        <Icon width="20px" height="20px">
            <slot name="icon-left" />
        </Icon>
    {/if}

    {#if loading}
        <span class="gw-btn__spinner">
            <Spinner
                color="--gw-text-primary-disabled"
                accessibilityLabel="Loading"
            />
        </span>
    {/if}

    {#if $$slots.default}
        <span class="gw-btn__label">
            <Text component="span" variant="body-md" fontWeight="medium" {tone}>
                <slot />
            </Text>
        </span>
    {/if}

    {#if $$slots["icon-right"]}
        <Icon width="20px" height="20px">
            <slot name="icon-right" />
        </Icon>
    {/if}
</UnstyledButton>

<style lang="postcss" global>
    @define-mixin button-spacing $btnPadding, $labelPadding {
        padding: $btnPadding;

        .gw-btn__label {
            padding-inline: $labelPadding;
        }
    }

    @define-mixin button-mode $bgColor, $color, $hoverBgColor {
        background-color: $bgColor;
        color: $color;

        @media (hover: hover) {
            &:hover {
                background-color: $hoverBgColor;
            }
        }
    }

    @define-mixin link-decoration $textDecoration, $underlineOffset,
        $textThickness {
        text-decoration: $textDecoration;
        text-underline-offset: $underlineOffset;
        text-decoration-thickness: $textThickness;
    }

    .gw-unstyled-btn.gw-btn {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: var(--gw-width-025) solid transparent;
        border-radius: var(--gw-p-border-radius-button);
        transition: all var(--gw-button-transition-duration) ease;

        &--full-width {
            width: 100%;
        }

        &:focus-visible {
            box-shadow: 0 0 0 2px hsla(214, 100%, 41%, 1);
            outline: none;
        }

        &--size-regular {
            @mixin button-spacing
                    calc(var(--gw-space-300) - var(--gw-width-025)),
                var(--gw-space-200);
        }

        &--size-small {
            @mixin button-spacing
                    calc(var(--gw-space-150) - var(--gw-width-025)),
                var(--gw-space-100);
        }

        &--mode-primary {
            @mixin button-mode var(--gw-button__main-color),
                var(--gw-button__text-color),
                var(--gw-button__main-highlight-color);
        }

        &--mode-secondary {
            @mixin button-mode var(--gw-bg-fill), inherit, var(--gw-bg-fill);
            border-color: var(--gw-border-default);

            @media (hover: hover) {
                &:hover {
                    border-color: var(--gw-border-hover);
                }
            }

            &:active {
                border-color: var(--gw-border-active);
            }
        }

        &--mode-tertiary {
            @mixin button-mode transparent, inherit,
                var(--gw-bg-fill-tertiary-hover);

            &.gw-btn--disabled {
                background-color: transparent;
            }

            &:active {
                background-color: var(--gw-bg-fill-tertiary-active);
            }
        }

        &--mode-link {
            gap: var(--gw-space-100);
            border: none;

            @mixin button-spacing var(--gw-space-0), var(--gw-space-0);

            &.gw-btn--disabled {
                background-color: transparent;
            }

            &:focus-visible {
                @mixin link-decoration underline, 10%, 10%;
                border-radius: var(--gw-border-radius-100);
            }

            @media (hover: hover) {
                &:hover {
                    @mixin link-decoration underline, 10%, 10%;
                }
            }
        }

        &--disabled {
            background-color: var(--gw-bg-fill-disabled);
            color: var(--gw-text-primary-disabled);
            user-select: none;
            pointer-events: none;
            border-color: transparent;
        }

        &--loading {
            color: transparent;
        }
    }

    .gw-btn {
        &__spinner {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            line-height: var(--gw-font-line-height-300);
        }
    }
</style>
